// @flow

import * as React from 'react';
import {useContext, useEffect, useMemo, useState} from 'react';
import {createBrowserHistory, createMemoryHistory} from 'history';
import {usePreviousValue} from 'docs/hooks';
import {LinkInfo} from 'docs/data';
import {contextStubFunction, isBrowser} from 'docs/util';
import {Location} from './Location';
import {locationIsVSCode} from '../../util/locationIsVSCode';

export type SetLocationFunction = (location: string | $Shape<Location>) => void;
export type GetLinkInfoFunction = (url: string) => LinkInfo;

export type LocationContextType = {
  baseUrl: string,
  getLinkInfo: GetLinkInfoFunction,
  location: Location,
  previousLocation: Location,
  pushLocation: SetLocationFunction,
  replaceLocation: SetLocationFunction,
};

const LocationContext = React.createContext<LocationContextType>({
  baseUrl: '',
  getLinkInfo: contextStubFunction('Location'),
  location: ({}: any),
  previousLocation: ({}: any),
  pushLocation: contextStubFunction('Location'),
  replaceLocation: contextStubFunction('Location'),
});

type LocationProviderProps = {|
  baseUrl: string,
  children: React.Node,
  initialPath: string,
|};

export const LocationProvider = ({
  baseUrl,
  children,
  initialPath,
}: LocationProviderProps) => {
  // If we're in the browser, use the actual History API; otherwise, use a polyfill.
  const history = useMemo(() => {
    return isBrowser
      ? createBrowserHistory()
      : createMemoryHistory({initialEntries: [initialPath]});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  const [historyLocation, setHistoryLocation] = useState(history.location);

  // Listen for changes to the location, and update it in our state. This allows
  // consumers of the context to update in response to location changes without
  // having to register callbacks of their own.
  useEffect(() => {
    return history.listen(setHistoryLocation);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  // The Location object which is stored in the LocationContext isn't the one
  // returned by the History API. We wrap it to provide a nicer interface.
  const location = Location.fromHistoryLocation(historyLocation, baseUrl);

  const previousLocation = usePreviousValue(location);

  const context = {
    baseUrl,
    location,
    previousLocation,
    getLinkInfo: (url: string) => new LinkInfo(url, baseUrl),
    pushLocation: (diff) => {
      if (locationIsVSCode(location)) {
        return;
      }

      history.push(location.derive(diff).toHistoryLocation());
    },
    replaceLocation: (diff) => {
      if (locationIsVSCode(location)) {
        return;
      }

      history.replace(location.derive(diff).toHistoryLocation());
    },
  };

  return (
    <LocationContext.Provider value={context}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  return useContext(LocationContext);
};
